import { useEffect, useRef, useState } from "react";
import { useUserAuth } from "../context/AuthContext";
import { ITokenPrefillResponse } from "../models/LoggedOutFormData";

export type TokenPrefillPayload = {
  data: ITokenPrefillResponse;
  updatedAt: string;
};

export function useTokenPrefillHook(
  token: string | undefined,
  getPrefillDataFromTokenFn: ((token: string) => Promise<ITokenPrefillResponse>) | undefined
): TokenPrefillPayload | undefined {
  const [payload, setPayload] = useState<TokenPrefillPayload | undefined>(
    undefined
  );
  const { user, loading: authLoading } = useUserAuth();
  const didLoadRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      if (authLoading) return;
      if (user) return;
      if (didLoadRef.current) return;

      if (!token) {
        didLoadRef.current = true;
        return;
      }

      if (!getPrefillDataFromTokenFn) {
        didLoadRef.current = true;
        return;
      }

      const normalizeDate = (isoStr?: string) => {
        return isoStr ? isoStr.split("T")[0] : undefined;
      };

      try {
        // Fetch data from API
        const apiData = await getPrefillDataFromTokenFn(token);

        if (!apiData || Object.keys(apiData).length === 0) {
          didLoadRef.current = true;
          return;
        }

        // Map API response to survey field structure
        const surveyData = {
          title: apiData.title,
          lastName: apiData.lastName,
          email: apiData.email,
          postcode: apiData.postcode,
          dateOfBirth: normalizeDate(apiData.dateOfBirth),
          primaryPhone: apiData.mobilePhone,
          transferToID: apiData.leadID,
          tk: apiData.token,
          sessionID: apiData.sessionID,
          annuity:
            apiData.annuityTotalFundAmount || apiData.retirementDate
              ? {
                  pensionFund: apiData.annuityTotalFundAmount
                    ? String(apiData.annuityTotalFundAmount)
                    : undefined,
                  plannedRetirementDate: normalizeDate(apiData.retirementDate),
                }
              : undefined,
        };

        setPayload({
          data: surveyData,
          updatedAt: new Date().toISOString(),
        });
      } catch (error) {
        // do nothing if error
      } finally {
        didLoadRef.current = true;
      }
    };

    load();
  }, [authLoading, user, token, getPrefillDataFromTokenFn]);

  return payload;
}
